// Shared wave engine: moves every anchor point of SVG paths along a sine wave.
// Used by ribbon.js (stroked ribbons) and wave.js (filled background waves).

const waveRamp = 1500;
const waveFps = 30;
const frameInterval = 1000 / waveFps;

// For each command, tells which anchor point each coordinate pair belongs to:
// -1 = the previous point (outgoing handle), 0 = the command's own point
const pairOwners = {
    M: [0],
    L: [0],
    T: [0],
    C: [-1, 0, 0],
    S: [0, 0],
    Q: [0, 0],
};

// Splits an absolute-coordinate path (M, L, C, S, Q, T) and links each handle to its
// anchor point, so both move together and the curve stays smooth
function parsePath(d) {
    const tokens = d.match(/[a-zA-Z]|-?\d*\.?\d+(?:e-?\d+)?/g);
    const segments = [];
    const anchors = [];

    tokens.forEach((token) => {
        if (/[a-zA-Z]/.test(token)) {
            segments.push({ command: token, values: [], pairs: [] });
        } else {
            segments[segments.length - 1].values.push(parseFloat(token));
        }
    });

    segments.forEach((segment) => {
        const owners = pairOwners[segment.command];
        if (!owners) {
            return;
        }
        const size = owners.length * 2;
        for (let i = 0; i + size <= segment.values.length; i += size) {
            const groupAnchor = anchors.length;
            owners.forEach((owner, j) => {
                segment.pairs.push({
                    x: segment.values[i + j * 2],
                    y: segment.values[i + j * 2 + 1],
                    anchor: Math.max(groupAnchor + owner, 0),
                });
            });
            const end = segment.pairs[segment.pairs.length - 1];
            anchors.push({ x: end.x, y: end.y });
        }
    });

    return { segments, anchors };
}

function getSetting($element, name, fallback) {
    const value = parseFloat($element.dataset[name]);
    return Number.isNaN(value) ? fallback : value;
}

// Settings are read from data-wave-amplitude, data-wave-length and data-wave-duration,
// with the given defaults as fallback
export function createWave($element, defaults) {
    const amplitudeSetting = getSetting($element, 'waveAmplitude', defaults.amplitude);
    const lengthSetting = getSetting($element, 'waveLength', defaults.length);
    const durationSetting = getSetting($element, 'waveDuration', defaults.duration);
    const paths = Array.from($element.querySelectorAll('path')).map(($path) => ({
        $path,
        $svg: $path.ownerSVGElement,
        scale: 0,
        ...parsePath($path.getAttribute('d')),
    }));
    let startTime = null;

    // Measures the SVG scale only when its size changes (0 = hidden SVG)
    const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
            const path = paths.find(({ $svg }) => $svg === entry.target);
            const { width } = entry.contentRect;
            path.scale = width ? path.$svg.viewBox.baseVal.width / width : 0;
        });
    });
    paths.forEach(({ $svg }) => resizeObserver.observe($svg));

    return {
        reset() {
            startTime = null;
        },
        render(time) {
            if (startTime === null) {
                startTime = time;
            }
            const elapsed = time - startTime;
            const ramp = Math.min(elapsed / waveRamp, 1);
            const phase = (elapsed / durationSetting) * Math.PI * 2;

            paths.forEach(({
                $path, scale, segments, anchors,
            }) => {
                if (!scale) {
                    return;
                }
                const amplitude = amplitudeSetting * scale * ramp;
                const k = (Math.PI * 2) / (lengthSetting * scale);
                const offsets = anchors.map(({ x, y }) => ({
                    x: amplitude * 0.5 * Math.cos(k * y + phase),
                    y: amplitude * Math.sin(k * x - phase),
                }));

                const d = segments.map(({ command, values, pairs }) => {
                    if (!pairs.length) {
                        return command + values.join(' ');
                    }
                    return command + pairs.map(({ x, y, anchor }) => {
                        const offset = offsets[anchor];
                        return `${(x + offset.x).toFixed(2)} ${(y + offset.y).toFixed(2)}`;
                    }).join(' ');
                }).join('');

                $path.setAttribute('d', d);
            });
        },
    };
}

// A single animation loop for every visible wave on the page
const activeWaves = new Set();
let frame = null;
let lastTime = 0;

function loop(time) {
    frame = window.requestAnimationFrame(loop);

    if (time - lastTime < frameInterval - 1) {
        return;
    }
    lastTime = time;
    activeWaves.forEach((wave) => wave.render(time));
}

export function play(wave) {
    if (activeWaves.has(wave)) {
        return;
    }
    wave.reset();
    activeWaves.add(wave);
    if (frame === null) {
        lastTime = 0;
        frame = window.requestAnimationFrame(loop);
    }
}

export function pause(wave) {
    activeWaves.delete(wave);
    if (!activeWaves.size && frame !== null) {
        window.cancelAnimationFrame(frame);
        frame = null;
    }
}
